import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoOutlineRoundedIcon from "@mui/icons-material/InfoOutlineRounded";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../api/Axios";
import DataLoader from "../../../shared/DataLoader/DataLoader";
import CommonButton from "../../../../src/shared/button/Button";

const ProgressDistrict: React.FC = () => {
  const { t } = useTranslation("home");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const { stateId } = useParams();
  const [districtData, setDistrictData] = useState([]);
  const [districtDataLoading, setDistrictDataLoading] = useState(false);
  const [stateName, setStateName] = useState("");
  const [districtsList, setDistrictsList] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch state name
  useEffect(() => {
    const fetchStateName = async () => {
      if (!stateId) return;
      try {
        const response = await API.get(`master/master-states`);
        const found = response?.data?.data?.find(
          (s: any) => String(s.id) === String(stateId)
        );
        setStateName(found?.name || "");
      } catch {
        setStateName("");
      }
    };
    fetchStateName();
  }, [stateId]);

  // Fetch districts for state
  useEffect(() => {
    const fetchDistricts = async () => {
      if (!stateId) return;
      setDistrictsLoading(true);
      try {
        const response = await API.get(
          `master/master-districts?state_id=${stateId}`
        );
        setDistrictsList(response?.data?.data || []);
      } catch {
        setDistrictsList([]);
      } finally {
        setDistrictsLoading(false);
      }
    };
    fetchDistricts();
  }, [stateId]);

  // Fetch district data
  const getDistrictData = async (districtId?: number) => {
    try {
      setDistrictDataLoading(true);
      let url = `web/survey-stats/district-wise?state_id=${stateId}`;
      if (districtId) url += `&district_id=${districtId}`;
      const response = await API.get(url);
      setDistrictData(response?.data?.data?.region_stats || []);
    } catch {
      setDistrictData([]);
    } finally {
      setDistrictDataLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (stateId) getDistrictData();
  }, [stateId]);

  const handleStateChange = (event) => {
    setState(event.target.value);
  };

  const handleDistrictChange = (event) => {
    setDistrict(event.target.value);
  };

  const handleApplyFilter = () => {
    if (district) {
      getDistrictData(Number(district));
    }
  };

  const handleResetFilter = () => {
    if (district) {
      setDistrict("");
      getDistrictData();
    }
  };

  return (
    <Box
      sx={{
        background: "transparent",
        pt: { xs: 3, md: "45px" },
        pb: { xs: 7, md: 10 },
        minHeight: "calc(100vh - 210px)",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", sm: "94%", md: "88%" } }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box
              sx={{
                background: "transparent",
                pb: { xs: 1, md: 2 },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                borderRadius: "8px",
                flexWrap: "wrap"
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? theme.palette.text.primary : "#0F202B",
                  fontSize: {
                    xs: `calc(34px * var(--dynamic-font-size, 1)) !important`,
                    md: `calc(48px * var(--dynamic-font-size, 1)) !important`,
                  },
                  lineHeight: 1.15,
                }}
              >
                {t("progress.title")}
              </Typography>

              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: {xs: "100%", sm: "auto"},
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDarkMode
                        ? theme.palette.text.primary
                        : "#0F202B",
                      fontWeight: 600,
                      fontSize: {
                        xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                        md: `calc(16px * var(--dynamic-font-size, 1)) !important`,
                      },
                      mb: 1,
                    }}
                  >
                    {t("progress.filters")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: {xs: "flex-end", sm: "flex-end", md: "unset"}, 
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{
                      width: {xs: "100%", sm: "auto"}
                    }}>
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: {xs: "100%", sm: "200px"}, 
                          maxWidth: {xs: "100%", sm: "200px"},
                          cursor: "not-allowed",
                          "& .Mui-disabled": { cursor: "not-allowed" },
                        }}
                      >
                        <InputLabel>{t("progress.selectState")}</InputLabel>
                        <Select
                          value={stateId || ""}
                          label={t("progress.selectState")}
                          disabled
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 200,
                              },
                            },
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          }}
                        >
                          <MenuItem value={stateId}>{stateName}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{
                      width: {xs: "100%", sm: "auto"}
                    }}>
                      <FormControl
                        size="small"
                        sx={{ 
                          minWidth: {xs: "100%", sm: "200px"}, 
                          maxWidth: {xs: "100%", sm: "200px"}
                        }}
                      >
                        <InputLabel>{t("progress.selectDistrict")}</InputLabel>
                        <Select
                          value={district}
                          onChange={handleDistrictChange}
                          label={t("progress.selectDistrict")}
                          disabled={districtsLoading}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 200,
                              },
                            },
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          }}
                        >
                          {districtsList.map((districtItem: any) => (
                            <MenuItem
                              key={districtItem.id}
                              value={districtItem.id}
                            >
                              {districtItem.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleApplyFilter}
                        sx={{
                          px: 3,
                          color: isDarkMode ? "#d0d0d0" : "#263b80",
                          borderColor: isDarkMode ? "#9b9b9b" : "#263b80",
                          borderRadius: "999px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "40px",
                        }}
                      >
                        {t("progress.applyFilter")}
                      </Button>
                    </Box>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleResetFilter}
                        sx={{
                          px: 3,
                          color: isDarkMode ? "#d0d0d0" : "#263b80",
                          borderColor: isDarkMode ? "#9b9b9b" : "#263b80",
                          borderRadius: "999px",
                          fontSize: "14px",
                          fontWeight: 500,
                          height: "40px",
                        }}
                      >
                        {t("progress.reset")}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Box>
          </Grid>
          <Grid
            size={12}
            sx={{
              boxShadow: "0px 1.24px 2.47px -1.24px #0000001A",
              border: `1px solid ${isDarkMode ? "#5f6666" : "#5de4d2"}`,
              borderRadius: { xs: "24px", md: "44px" },
              overflow: "hidden",
              background: isDarkMode
                ? "linear-gradient(180deg, #292d2f 0%, #25322c 100%)"
                : "linear-gradient(180deg, rgba(255,255,255,.96) 0%, rgba(235,255,235,.78) 100%)",
            }}
          >
            <Box
              sx={{
                padding: {xs: "15px", md: "25px"},
                background: "transparent",
                borderRadius: 0,
                borderBottom: "1px solid#F3F4F6",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? theme.palette.text.primary : "#1E2939",
                  fontSize: {
                    xs: `calc(16px * var(--dynamic-font-size, 1)) !important`,
                    md: `calc(20px * var(--dynamic-font-size, 1)) !important`,
                  },
                  mb: 0,
                }}
              >
                {t("progress.stateDistrictProgress")} {stateName} <br />
                {t("progress.districtWiseProgressTracker")}
              </Typography>
              <CommonButton
                text={t("progress.back")}
                sx={{
                  minWidth: "100px",
                  maxHeight: "40px",
                  fontSize: "14px",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  navigate("/progress");
                }}
              />
            </Box>
            {districtDataLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "350px",
                  background: "transparent",
                  borderRadius: 0,
                }}
              >
                <DataLoader />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 0,
                  padding: { xs: "10px 15px 22px", md: "0 25px 30px" },
                  background: "transparent",
                  overflowX: "auto",
                }}
              >
                <Table className="progress-status-table" sx={{ minWidth: 720 }}>
                  <TableHead
                    sx={{
                      "& .MuiTableCell-root": {
                        fontWeight: 700,
                        // backgroundColor: isDarkMode
                        //   ? theme.palette.background.paper
                        //   : "#fdfefe",
                        color: isDarkMode
                          ? theme.palette.text.primary
                          : "#364153",
                        fontSize: {
                          xs: `calc(12px * var(--dynamic-font-size, 1)) !important`,
                          md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                        },
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>{t("progress.districtName")}</TableCell>
                      <TableCell>{t("progress.initiated")}</TableCell>
                      <TableCell>{t("progress.registered")}</TableCell>
                      <TableCell>{t("progress.completed")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(districtData.length > 0 ? districtData : []).map(
                      (row: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                // backgroundColor: isDarkMode
                                //   ? theme.palette.background.paper
                                //   : "#fdfefe",
                                color: isDarkMode
                                  ? theme.palette.text.primary
                                  : "#364153",
                                fontSize: {
                                  xs: `calc(12px * var(--dynamic-font-size, 1)) !important`,
                                  md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                                },
                              }}
                            >
                              {row?.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.initiated}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.warning.dark
                                  : "#FFEDD4",
                                color: isDarkMode
                                  ? theme.palette.warning.contrastText
                                  : "#CA3500",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: {xs: "12px", md: "14px"},
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.registered}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.action.selected
                                  : "#F1F5F9",
                                color: isDarkMode
                                  ? theme.palette.text.primary
                                  : "#4A5565",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: {xs: "12px", md: "14px"},
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row?.completed}
                              sx={{
                                backgroundColor: isDarkMode
                                  ? theme.palette.success.dark
                                  : "#DCFCE7",
                                color: isDarkMode
                                  ? theme.palette.success.contrastText
                                  : "#008236",
                                fontWeight: 400,
                                borderRadius: "10px",
                                fontSize: {xs: "12px", md: "14px"},
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProgressDistrict;
